import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import WizardStepContainer from '../WizardStepContainer';
import { WizardStylesContext } from '../WizardStylesContext';
import { useIntl } from 'react-intl';
import { formCommentLink, formInvestibleLink, navigate } from '../../../utils/marketIdPathFunctions';
import CommentAdd, { hasCommentValue } from '../../Comments/CommentAdd';
import { useHistory } from 'react-router';
import { getPageReducerPage, usePageStateReducer } from '../../PageState/pageStateHooks';
import { InvestiblesContext } from '../../../contexts/InvestibesContext/InvestiblesContext';
import { getInvestible } from '../../../contexts/InvestibesContext/investiblesContextHelper';
import {
  changeInvestibleStageOnCommentClose,
  doesCommentResolutionRestoreStage,
  getWorkflowStageContext
} from '../../../utils/commentFunctions';
import {
  ISSUE_TYPE,
  QUESTION_TYPE,
  REPLY_TYPE,
  REPORT_TYPE,
  SUGGEST_CHANGE_TYPE,
  TODO_TYPE
} from '../../../constants/comments';
import {
  getBlockedStage, getFurtherWorkStage, getFullStage,
  getRequiredInputStage
} from '../../../contexts/MarketStagesContext/marketStagesContextHelper';
import { MarketStagesContext } from '../../../contexts/MarketStagesContext/MarketStagesContext';
import JobDescription from '../../InboxWizards/JobDescription';
import { addCommentToMarket, getComment, getInvestibleComments } from '../../../contexts/CommentsContext/commentsContextHelper';
import { CommentsContext } from '../../../contexts/CommentsContext/CommentsContext';
import _ from 'lodash';
import { resolveComment } from '../../../api/comments';
import { removeMessagesForCommentId } from '../../../utils/messageUtils';
import { OperationInProgressContext } from '../../../contexts/OperationInProgressContext/OperationInProgressContext';
import { NotificationsContext } from '../../../contexts/NotificationsContext/NotificationsContext';
import GravatarGroup from '../../Avatars/GravatarGroup';
import { getHumanPresences } from '../../../contexts/MarketPresencesContext/marketPresencesHelper';
import { getMarket } from '../../../contexts/MarketsContext/marketsContextHelper';
import { MarketsContext } from '../../../contexts/MarketsContext/MarketsContext';
import { getMarketInfo } from '../../../utils/userFunctions';

export function hasJobComment(groupId, investibleId, commentType, unSentInvestibleComments) {
  if (!_.isEmpty(unSentInvestibleComments?.find((comment) => comment.comment_type === commentType))) {
    return true;
  }
  return hasCommentValue(groupId, undefined, 'JobCommentAdd', investibleId,
    `jobComment${commentType}`);
}

function AddCommentStep (props) {
  const { investibleId, marketId, useType, updateFormData = () => {}, formData = {}, resolveId, groupId, currentStageId,
    assigned, onFinishCreation, subscribed, presences, decisionInvestibleId, decisionMarketId, isNote, isInline } = props;
  const intl = useIntl();
  const classes = useContext(WizardStylesContext);
  const [marketStagesState] = useContext(MarketStagesContext);
  const [commentState, commentDispatch] = useContext(CommentsContext);
  const [investiblesState, investiblesDispatch] = useContext(InvestiblesContext);
  const [, setOperationRunning] = useContext(OperationInProgressContext);
  const [marketsState] = useContext(MarketsContext);
  const [messagesState, messagesDispatch] = useContext(NotificationsContext);
  const requiresInputStage = getRequiredInputStage(marketStagesState, marketId) || {};
  const blockingStage = getBlockedStage(marketStagesState, marketId) || {};
  const furtherWorkStage = getFurtherWorkStage(marketStagesState, marketId) || {};
  const history = useHistory();
  const [commentAddStateFull, commentAddDispatch] = usePageStateReducer('addDecisionCommentWizard');
  const [commentAddState, updateCommentAddState, commentAddStateReset] =
    getPageReducerPage(commentAddStateFull, commentAddDispatch, investibleId);
  const isAssistance = [ISSUE_TYPE, QUESTION_TYPE, SUGGEST_CHANGE_TYPE].includes(useType);
  const inAssistanceStage = [requiresInputStage.id, blockingStage.id].includes(currentStageId);
  const inFurtherWorkStage = currentStageId === furtherWorkStage.id;
  const investibleComments = getInvestibleComments(investibleId, marketId, commentState);
  // B-all-675: a task made from an option is about that one option, so the job's other tasks don't show
  const comments = useType === TODO_TYPE && !decisionInvestibleId ? investibleComments?.filter((comment) =>
    [TODO_TYPE, REPLY_TYPE].includes(comment.comment_type)) : undefined;
  const { useCompression } = formData;
  const isResolve = !_.isEmpty(resolveId);
  const myPresence = presences?.find((presence) => presence.current_user);
  const userId = myPresence?.id;
  const userIsAssigned = assigned?.includes(userId);
  // The AI user gets its notifications through the poke queue, so showing it in a "notifies" list
  // just confuses (B-all-500)
  const subscribedNotMe = getHumanPresences(subscribed).filter((presence) => presence.id !== userId);
  const noSubscribedToSendTo = _.isEmpty(subscribedNotMe) || inFurtherWorkStage;

  function onSave(comment) {
    if (comment.is_sent) {
      navigate(history, formCommentLink(marketId, groupId, investibleId, comment.id));
    } else {
      onFinishCreation();
      updateFormData({inlineMarketId: comment.inline_market_id, commentId: comment.id, marketId, investibleId,
        groupId});
    }
  }

  function onReportResolveOnly() {
    return resolveComment(marketId, resolveId)
      .then((comment) => {
        addCommentToMarket(comment, commentState, commentDispatch);
        removeMessagesForCommentId(resolveId, messagesState, messagesDispatch);
        setOperationRunning(false);
        navigate(history, formCommentLink(marketId, groupId, investibleId, resolveId));
      });
  }

  // B-all-485: Make task is also reachable from an option of an already resolved question -
  // then the 'Add and resolve' button must not show since resolving again would error
  const fromDecisionMarket = getMarket(marketsState, decisionMarketId);
  const questionAlreadyResolved = decisionInvestibleId ?
    getComment(commentState, marketId, fromDecisionMarket?.parent_comment_id)?.resolved : undefined;

  function onCreateTaskQuestionResolve() {
    const { parent_comment_id: parentCommentId } = fromDecisionMarket;
    const question = getComment(commentState, marketId, parentCommentId);
    const inv = getInvestible(investiblesState, investibleId);
    const marketInfo = getMarketInfo(inv, marketId) || {};
    const restoresFormerStage = !!inv && !!question && doesCommentResolutionRestoreStage(
      question,
      investibleComments,
      marketInfo.assigned || assigned,
      presences,
      getWorkflowStageContext(
        marketStagesState,
        marketId,
        getFullStage(marketStagesState, marketId, currentStageId),
        marketInfo.former_stage_id
      )
    );
    return resolveComment(marketId, parentCommentId)
      .then((comment) => {
        addCommentToMarket(comment, commentState, commentDispatch);
        if (restoresFormerStage) {
          changeInvestibleStageOnCommentClose(inv.market_infos, inv.investible, investiblesDispatch,
            comment.updated_at, marketStagesState, marketId);
        }
        removeMessagesForCommentId(parentCommentId, messagesState, messagesDispatch);
      });
  }

  // B-all-675: a task from an option keeps its own draft. The job's shared task draft - even one left
  // empty by the last task added - is stored state, which the editor prefers to the prefilled option link.
  const draftName = `jobComment${useType}${decisionInvestibleId || ''}`;
  const movingJob = isAssistance && !inAssistanceStage && userIsAssigned;
  const optionUrl = `${formInvestibleLink(marketId, investibleId)}#option${decisionInvestibleId}`;
  const titleByType = {
    [TODO_TYPE]: 'New Task',
    [QUESTION_TYPE]: 'New Question',
    [SUGGEST_CHANGE_TYPE]: 'New Suggestion',
    [ISSUE_TYPE]: 'New Blocker',
    [REPORT_TYPE]: 'New Progress Report'
  };
  const wizardTitle = isNote ? 'New Note' : (titleByType[useType] || 'New comment');
  // Inline so the "notifies ... unless use @ mentions" sentence stays on one line - AvatarGroup's
  // root is otherwise a block flex div that splits the sentence over three lines (B-all-500)
  const subscribedGravatars =
    <span style={{display: 'inline-flex', verticalAlign: 'middle', marginLeft: '0.3rem', marginRight: '0.3rem'}}>
      <GravatarGroup users={subscribedNotMe}/>
    </span>;
  return (
    <WizardStepContainer
      {...props}
    >
      {/* J-all-325 (Q-all-148, O-5): a concise form-style heading instead of the casual "What is your task?",
         with tighter typography than the shared 35px introText. */}
      <Typography className={classes.introText} style={{ fontSize: '24px', lineHeight: '30px', marginTop: 0,
        marginBottom: '1.25rem' }}>
        {wizardTitle}
      </Typography>
      {movingJob && !noSubscribedToSendTo && (
        <Typography className={classes.introSubText} variant="subtitle1">
          Opening this {intl.formatMessage({ id: `${useType.toLowerCase()}Simple` })} moves the job to
          Next / Assistance.
          {subscribedGravatars}
          notified unless use @ mentions.
        </Typography>
      )}
      {movingJob && noSubscribedToSendTo && (
        <Typography className={classes.introSubText} variant="subtitle1">
          Opening this {intl.formatMessage({ id: `${useType.toLowerCase()}Simple` })} moves the job to
          Next / Assistance.
        </Typography>
      )}
      {useType === TODO_TYPE && !decisionInvestibleId && (
        <Typography className={classes.introSubText} variant="subtitle1">
          Tasks hold grouped tasks, notes, and in progress status.
        </Typography>
      )}
      {useType === TODO_TYPE && decisionInvestibleId && (
        <Typography className={classes.introSubText} variant="subtitle1">
          The link to the below option is prefilled in this task.
        </Typography>
      )}
      {useType === REPORT_TYPE && !isResolve && noSubscribedToSendTo && (
        <Typography className={classes.introSubText} variant="subtitle1">
          You must use @ mentions for this report to notify anyone.
        </Typography>
      )}
      {useType === ISSUE_TYPE && !inFurtherWorkStage && !noSubscribedToSendTo && (
        <Typography className={classes.introSubText} variant="subtitle1">
          {subscribedGravatars}
          notified unless use @ mentions.
        </Typography>
      )}
      {useType === ISSUE_TYPE && inFurtherWorkStage && !noSubscribedToSendTo && (
        <Typography className={classes.introSubText} variant="subtitle1">
          Jobs with blocking issues are always not ready.
          {subscribedGravatars}
          notified unless use @ mentions.
        </Typography>
      )}
      {useType === ISSUE_TYPE && inFurtherWorkStage && (
        <Typography className={classes.introSubText} variant="subtitle1">
          Jobs with blocking issues are always not ready. Use @ mentions to send notifications.
        </Typography>
      )}
      {[QUESTION_TYPE, SUGGEST_CHANGE_TYPE].includes(useType) && inFurtherWorkStage && (
        <Typography className={classes.introSubText} variant="subtitle1">
          Use @ mentions or options / voting to send notifications from backlog.
        </Typography>
      )}
      {useType === REPORT_TYPE && !isResolve && !noSubscribedToSendTo && !isNote && (
        <Typography className={classes.introSubText} variant="subtitle1">
          {subscribedGravatars}
          notified unless use @ mentions to require and only notify specific reviewers.
        </Typography>
      )}
      {useType === REPORT_TYPE && isResolve && !isNote && (
        <Typography className={classes.introSubText} variant="subtitle1">
          Opening a new progress report automatically resolves older reports. If you don't have a new report choose
          'Resolve only' below.
        </Typography>
      )}
      {isNote && (
        <Typography className={classes.introSubText} variant="subtitle1">
          Notes do not notify unless you use @ mentions.
        </Typography>
      )}
      {useType === QUESTION_TYPE && !movingJob && !noSubscribedToSendTo && (
        <Typography className={classes.introSubText} variant="subtitle1">
          This question notifies
          {subscribedGravatars}
          unless use @ mentions. Add options to start voting on possible answers to this question.
        </Typography>
      )}
      {useType === SUGGEST_CHANGE_TYPE && !movingJob && !noSubscribedToSendTo && (
        <Typography className={classes.introSubText} variant="subtitle1">
          This suggestion notifies
          {subscribedGravatars}
          unless use @ mentions.
        </Typography>
      )}
      {useType === QUESTION_TYPE && !movingJob && noSubscribedToSendTo && (
        <Typography className={classes.introSubText} variant="subtitle1">
          Add options to start voting on possible answers to this question.
        </Typography>
      )}
      {/* J-all-325 (T-all-2187): inside the job container the name and assignment already show in the
         job nav, so don't repeat the JobDescription header here. */}
      {!isInline && (
        <JobDescription marketId={marketId} investibleId={investibleId} comments={comments}
                        useCompression={useCompression}
                        toggleCompression={() => updateFormData({ useCompression: !useCompression })}/>
      )}
      {decisionInvestibleId && (
        <div style={{marginTop: '2rem'}}>
          <JobDescription marketId={decisionMarketId} investibleId={decisionInvestibleId} useCompression={useCompression} useJobLink={optionUrl}
            toggleCompression={() => updateFormData({ useCompression: !useCompression })}/>
        </div>
      )}
      <div className={classes.borderBottom}/>
      <CommentAdd
        nameKey="JobCommentAdd"
        type={useType}
        wizardProps={{ ...props, isAddWizard: true, isResolve, isNote: isNote,
          questionResolved: questionAlreadyResolved,
          onResolve: decisionInvestibleId ? onCreateTaskQuestionResolve : onReportResolveOnly }}
        commentAddState={commentAddState}
        updateCommentAddState={updateCommentAddState}
        commentAddStateReset={commentAddStateReset}
        marketId={marketId}
        groupId={groupId}
        fromInvestibleId={investibleId}
        fromDecisionInvestibleId={decisionInvestibleId}
        onSave={onSave}
        nameDifferentiator={draftName}
      />
    </WizardStepContainer>
  );
}

AddCommentStep.propTypes = {
  updateFormData: PropTypes.func,
  formData: PropTypes.object
};

export default AddCommentStep;
